import { useEffect, useCallback, useRef } from 'react'
import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Underline from '@tiptap/extension-underline'
import TextAlign from '@tiptap/extension-text-align'
import Placeholder from '@tiptap/extension-placeholder'
import Image from '@tiptap/extension-image'
import { Table } from '@tiptap/extension-table'
import { TableRow } from '@tiptap/extension-table-row'
import { TableCell } from '@tiptap/extension-table-cell'
import { TableHeader } from '@tiptap/extension-table-header'
import { TextStyle } from '@tiptap/extension-text-style'
import { Color } from '@tiptap/extension-color'
import Highlight from '@tiptap/extension-highlight'
import { motion } from 'framer-motion'
import { cn } from '@/utils/cn'
import { useEditorStore, type EditorIssue } from '@/stores/editorStore'
import { useTranslation } from '@/stores/i18nStore'
import { useAnalytics } from '@/hooks/useAnalytics'
import { SpellcheckExtension } from './extensions/SpellcheckExtension'
import { AutocompleteExtension } from './extensions/AutocompleteExtension'
import { EditorToolbar } from './EditorToolbar'
import { EditorSidebar } from './EditorSidebar'
import { EditorContextMenu } from './EditorContextMenu'

export function WordEditor() {
  const { t } = useTranslation()
  const { trackDocumentCreate, trackDocumentSave } = useAnalytics()
  const {
    currentDocument,
    updateDocumentContent,
    updateDocumentTitle,
    createNewDocument,
    saveDocument,
    showSidebar,
    fontSize,
    fontFamily,
    setIssues,
    clearIssues
  } = useEditorStore()

  // Ref for context menu
  const editorContainerRef = useRef<HTMLDivElement>(null)

  // Callback when spellcheck finds errors - update sidebar
  const handleSpellcheck = useCallback((misspelled: Array<{ word: string; suggestions: string[] }>) => {
    if (misspelled.length > 0) {
      const issues: EditorIssue[] = misspelled.map((item, index) => ({
        id: `spelling-${index}-${Date.now()}`,
        type: 'spelling' as const,
        position: { from: 0, to: 0 }, // Position handled by decorations
        text: item.word,
        message: t('spelling') + ': ' + item.word,
        suggestions: item.suggestions
      }))
      setIssues(issues)
    } else {
      clearIssues()
    }
  }, [setIssues, clearIssues, t])

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3, 4, 5, 6]
        }
      }),
      Underline,
      TextAlign.configure({
        types: ['heading', 'paragraph']
      }),
      Placeholder.configure({
        placeholder: t('startTyping')
      }),
      Image.configure({
        inline: true,
        allowBase64: true
      }),
      Table.configure({
        resizable: true
      }),
      TableRow,
      TableCell,
      TableHeader,
      TextStyle,
      Color,
      Highlight.configure({
        multicolor: true
      }),
      // Spellcheck extension with inline underlines
      SpellcheckExtension.configure({
        debounce: 1000,
        onSpellcheck: handleSpellcheck
      }),
      // Autocomplete extension with ghost text
      AutocompleteExtension.configure({
        minChars: 2,
        debounce: 300,
        acceptKey: 'Tab'
      })
    ],
    content: currentDocument?.content || '',
    editorProps: {
      attributes: {
        class: 'prose prose-zinc dark:prose-invert max-w-none focus:outline-none min-h-[500px] font-ethiopic',
        style: `font-size: ${fontSize}px; font-family: "${fontFamily}", sans-serif;`
      }
    },
    onUpdate: ({ editor }) => {
      const html = editor.getHTML()
      updateDocumentContent(html)
    }
  })

  // Create new document on mount if none exists
  useEffect(() => {
    if (!currentDocument) {
      createNewDocument()
      trackDocumentCreate()
    }
  }, [currentDocument, createNewDocument, trackDocumentCreate])

  // Update editor content when document changes
  useEffect(() => {
    if (editor && currentDocument && editor.getHTML() !== currentDocument.content) {
      editor.commands.setContent(currentDocument.content)
    }
  }, [editor, currentDocument?.id])

  // Auto-save every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      if (currentDocument && editor) {
        saveDocument()
        const text = editor.getText()
        const wordCount = text.split(/\s+/).filter(w => w.length > 0).length
        trackDocumentSave(wordCount, text.length)
      }
    }, 30000)
    return () => clearInterval(interval)
  }, [currentDocument, saveDocument, editor, trackDocumentSave])

  // Keyboard shortcuts
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault()
        saveDocument()
        if (editor) {
          const text = editor.getText()
          const wordCount = text.split(/\s+/).filter(w => w.length > 0).length
          trackDocumentSave(wordCount, text.length)
        }
      }
    },
    [saveDocument, editor, trackDocumentSave]
  )

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [handleKeyDown])

  if (!editor) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-pulse text-zinc-400">{t('loading')}...</div>
      </div>
    )
  }

  return (
    <div className="h-full flex flex-col bg-white dark:bg-zinc-950">
      {/* Toolbar */}
      <EditorToolbar editor={editor} />

      {/* Main content area */}
      <div className="flex-1 flex overflow-hidden">
        {/* Editor area */}
        <div className="flex-1 overflow-auto">
          <div className="max-w-4xl mx-auto py-8 px-4">
            {/* Document title */}
            <input
              type="text"
              value={currentDocument?.title || ''}
              onChange={(e) => updateDocumentTitle(e.target.value)}
              placeholder={t('untitledDocument')}
              className="w-full text-3xl font-bold text-zinc-900 dark:text-white bg-transparent border-none outline-none mb-6 font-ethiopic placeholder:text-zinc-300 dark:placeholder:text-zinc-600"
            />

            {/* Editor content */}
            <motion.div
              ref={editorContainerRef}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className={cn(
                'bg-white dark:bg-zinc-900 rounded-lg shadow-sm',
                'border border-zinc-200 dark:border-zinc-800',
                'p-8 min-h-[600px]'
              )}
            >
              <EditorContent editor={editor} />
            </motion.div>

            {/* Word count */}
            <div className="mt-4 text-sm text-zinc-400 text-right">
              {editor.storage.characterCount?.words?.() || 0} {t('words')} •{' '}
              {editor.storage.characterCount?.characters?.() || editor.getText().length} {t('characters')}
            </div>
          </div>
        </div>

        {/* Sidebar */}
        {showSidebar && <EditorSidebar editor={editor} />}
      </div>

      {/* Context Menu for right-click */}
      <EditorContextMenu editor={editor} containerRef={editorContainerRef} />
    </div>
  )
}
